import AOS from 'aos';
import { useState, useLayoutEffect } from 'react';
import 'aos/dist/aos.css';
import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import '../Pages/style.css';
import AboutBanner from '../Images/aboutus.jpg';
import FacebookIcon from '../Images/facebook.png';
import TwitterIcon from '../Images/twitter.png';
import LinkedInIcon from '../Images/linkedin.png';
import InstagramIcon from '../Images/instagram.png';
import Footer from './Footer';

const About = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  useLayoutEffect(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    AOS.init({ duration: 1000, once: false });
  }, []);

  const purposeRef = useRef(null);
  const perspectiveRef = useRef(null);
  const businessRef = useRef(null);

  const scrollToSection = (section) => {
    if (section === 'Purpose') purposeRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (section === 'Perspective') perspectiveRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (section === 'Business') businessRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>About Mindron Meditech Pvt. Ltd. | Medical Device Company in Mumbai</title>
        <meta
          name="description"
          content="Learn about Mindron Meditech Pvt. Ltd., a Mumbai-based medical device company focused on innovative, safe, and reliable medical devices and diagnostic solutions."
        />
        <link rel="canonical" href="https://www.mindronmeditech.com/about" />
      </Helmet>

      <div style={{ fontFamily: 'Georgia, serif', color: '#222', overflowX: 'hidden' }}>

        {/* ── HERO BANNER ── */}
        <div style={{
          position: 'relative',
          width: '100%',
          height: isMobile ? '40vh' : '80vh',
          overflow: 'hidden',
        }}>
          <img
            src={AboutBanner}
            alt="About Us"
            style={{
              width: '100%',
              height: '100%',
              objectFit: isMobile ? 'fill' : 'cover',
              display: 'block',
            }}
          />
          {/* dark gradient overlay */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(0,20,60,0.55) 0%, transparent 60%)',
          }} />
          {/* "about us" label bottom-left */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '20px',
            transform: 'translateY(-50%)',
            color: 'black',
            fontSize: isMobile ? '20px' : '56px',
            fontWeight: 'bold',
            maxWidth: '90%',
            lineHeight: '1.4',
            zIndex: 2,
          }}>
            Welcome To
            <br />
            Mindron Meditech
            <br />
            Private Limited
          </div>
        </div>

        {/* ── ABOUT US SECTION ── */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '40px',
          padding: isMobile ? '40px 6%' : '60px 8%',
          alignItems: 'flex-start',
        }}>
          {/* Left: large heading */}
          <div style={{ flex: '0 0 auto', minWidth: '200px' }}>
            <div style={{ width: '48px', height: '5px', backgroundColor: '#cc0000', marginBottom: '14px' }} />
            <h1 style={{
              fontSize: isMobile ? '24px' : '38px',
              fontWeight: '800',
              color: '#003366',
              lineHeight: 1.1,
              margin: 0,
              letterSpacing: '-1px',
            }}>
              About
            </h1>
          </div>

          {/* Right: body text */}
          <div style={{ flex: 1, minWidth: '260px' }}>
            <p data-aos="fade-up" data-aos-duration="1000"
              style={{ fontSize: '16px', color: '#444', lineHeight: '1.8', marginTop: '8px' }}>
              <strong>Mindron Meditech Pvt. Ltd.</strong>  is an emerging Indian medicaldevice manufacturer and a forward-thinking healthcare technology company headquartered in Mumbai, India. Built on a strong foundation of industry experience and technical expertise, the company is dedicated to delivering innovative, safe, and reliable medical solutions that cater to the evolving needs of the healthcare sector.
            </p>
            <p data-aos="fade-up" data-aos-duration="1000" data-aos-delay="100"
              style={{ fontSize: '16px', color: '#444', lineHeight: '1.8' }}>
              Driven by a commitment to quality and continuous improvement, Mindron Meditech focuses on developing advanced medical technologies that enhance clinical efficiency, accuracy, and patient outcomes. The organization emphasizes research, innovation, and precision engineering to ensure that every product meets the highest standards of performance and reliability.
            </p>
          </div>
        </div>

        {/* ── MISSION & VISION (2-column cards) ── */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '28px',
          padding: isMobile ? '0 6% 40px' : '0 8% 60px',
        }}>
          {/* Mission Card */}
          <div data-aos="fade-right" data-aos-duration="1000"
            style={{
              flex: '1 1 280px',
              background: '#f7f9fc',
              border: '1px solid #e0e6ef',
              borderRadius: '10px',
              padding: '28px 28px 32px',
            }}>
            <div style={{ width: '40px', height: '5px', backgroundColor: '#cc0000', marginBottom: '12px' }} />
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#003366', margin: '0 0 14px' }}>Our Mission</h2>
            <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.75', margin: 0 }}>
              <strong>At Mindron Meditech</strong>, our mission is to empower healthcare providers with advanced, reliable, and cost-effective diagnostic technologies.
              We are dedicated to designing and manufacturing high-quality medical equipment that enhances clinical accuracy, supports better decision-making, and improves overall patient outcomes.
            </p>
          </div>

          {/* Vision Card */}
          <div data-aos="fade-left" data-aos-duration="1000"
            style={{
              flex: '1 1 280px',
              background: '#f7f9fc',
              border: '1px solid #e0e6ef',
              borderRadius: '10px',
              padding: '28px 28px 32px',
            }}>
            <div style={{ width: '40px', height: '5px', backgroundColor: '#cc0000', marginBottom: '12px' }} />
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#003366', margin: '0 0 14px' }}>Vision</h2>
            <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.75', margin: 0 }}>
              To become a globally trusted leader in medical technology by delivering innovative and precise diagnostic solutions that elevate healthcare standards and make advanced medical care accessible to every healthcare provider.
            </p>
          </div>
        </div>

        {/* ── R&D EXCELLENCE ── */}
        <div style={{ padding: isMobile ? '0 6% 36px' : '0 8% 44px' }}>
          <div style={{ width: '45px', height: '5px', backgroundColor: '#cc0000', marginBottom: '10px' }} />
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#003366', margin: '0 0 10px' }}>R&amp;D Excellence</h2>
          <p data-aos="fade-right" data-aos-duration="1000"
            style={{ fontSize: '16px', color: '#444', lineHeight: '1.8', margin: 0 }}>
            We are actively engaged in research and development for ICU and NICU product, supporting the
            'Make in India' initiative and contributing to the nation's self-reliance in critical medical technology.
          </p>
        </div>

        {/* ── OUR ACHIEVEMENTS ── */}
        <div style={{ padding: isMobile ? '0 6% 36px' : '0 8% 44px' }}>
          <div style={{ width: '45px', height: '5px', backgroundColor: '#cc0000', marginBottom: '10px' }} />
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#003366', margin: '0 0 10px' }}>Our Achievements</h2>
          <p data-aos="fade-right" data-aos-duration="1000"
            style={{ fontSize: '16px', color: '#444', lineHeight: '1.8' }}>
            At Mindron Meditech, our journey is defined by key milestones that reflect our commitment to quality, compliance, and innovation in the medical device industry:        </p>
          <ul data-aos="fade-right" data-aos-duration="1000"
            style={{ fontSize: '16px', color: '#444', lineHeight: '2', paddingLeft: '22px', margin: 0 }}>
            <li>DPIIT-Certified Startup – Recognized by the Government of India for innovation and growth potential</li>
            <li>ISO 13485 Certified – Adhering to internationally recognized quality management systems for medical devices</li>
            <li>CE Marked – Compliant with global safety, health, and performance standards</li>
            <li>CDSCO Approved – Fully aligned with India’s medical device regulatory requirements</li>
          </ul>
        </div>

        {/* ── WHAT DRIVES US ── */}
        <div style={{ padding: isMobile ? '0 6% 36px' : '0 8% 44px' }}>
          <div style={{ width: '45px', height: '5px', backgroundColor: '#cc0000', marginBottom: '10px' }} />
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#003366', margin: '0 0 10px' }}>What Drives Us</h2>
          <p data-aos="fade-right" data-aos-duration="1000"
            style={{ fontSize: '16px', color: '#444', lineHeight: '1.8', margin: 0 }}>
            <strong> At Mindron Meditech</strong>, we are driven by a strong commitment to the ‘Make in India’ vision, contributing to the development of a self-reliant and globally competitive medical device industry. Our focus is on designing and manufacturing high-quality, innovative medical technologies within India, catering to both domestic and international healthcare markets.
            With a purpose-driven approach and a passion for innovation, we strive to position India as a trusted hub for reliable, affordable, and advanced healthcare solutions.        </p>
        </div>

        {/* ── INVESTMENT PARTNER ── */}
        <div style={{ padding: isMobile ? '0 6% 44px' : '0 8% 56px' }}>
          <div style={{ width: '40px', height: '5px', backgroundColor: '#cc0000', marginBottom: '10px' }} />
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#003366', margin: '0 0 28px' }}>
            Investment Partner
          </h2>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '36px',
          }}>
            <img
              src={require('../Images/investor.jpg')}
              alt="Investor"
              style={{
                width: isMobile ? '100%' : '220px',
                height: '280px',
                borderRadius: '10px',
                objectFit: 'cover',
                objectPosition: 'top',

                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, minWidth: '240px' }}>
              <h3 data-aos="fade-right" data-aos-duration="1000"
                style={{ fontSize: '19px', fontWeight: '700', color: '#003366', margin: '0 0 12px' }}>
                Mr. Paresh Mangukiya
              </h3>
              <p data-aos="fade-right" data-aos-duration="1000"
                style={{ fontSize: '15px', color: '#555', lineHeight: '1.8', margin: 0 }}>
                Paresh - A seasoned investor with extensive experience across metal, real estate, diamond manufacturing, and hospitality sectors. He brings strategic insight, financial strength, and a strong global network, supporting Mindron Meditech’s growth and expansion. Their extensive network, financial strength, and commitment to innovation enable us to scale efficiently and seize new opportunities across markets. Aligned with our vision for excellence and sustainability, they are a trusted and valued partner in our journey toward long-term success.            </p>
            </div>
          </div>
        </div>

        {/* ── CEO ── */}
        <div style={{ padding: isMobile ? '0 6% 56px' : '0 8% 64px' }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap-reverse',
            alignItems: 'center',
            gap: '36px',
          }}>
            {/* Text left */}
            <div style={{ flex: '1 1 260px' }}>
              <div style={{ width: '45px', height: '5px', backgroundColor: '#cc0000', marginBottom: '10px' }} />
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#003366', margin: '0 0 6px' }}>CEO</h2>
              <h3 data-aos="fade-right" data-aos-duration="1000"
                style={{ fontSize: '19px', fontWeight: '700', color: '#003366', margin: '0 0 12px' }}>
                Mr. Rohit Bhanushali
              </h3>
              <p data-aos="fade-right" data-aos-duration="1000"
                style={{ fontSize: '15px', color: '#555', lineHeight: '1.8', margin: 0 }}>
                A visionary leader driving Mindron Meditech’s success, achieving key milestones, including DPIIT, ISO, CE, and CDSCO certifications. He leads with innovation, precision, and a strong focus on advancing healthcare technology. Backed by years of industry experience, he brings strategic foresight, operational excellence, and a deep understanding of healthcare technology. His leadership fosters a culture of integrity, continuous improvement, and customer-centric innovation, positioning Mindron Meditech as a trusted name in the medical device industry.            </p>
            </div>
            {/* Image right */}
            <img
              src={require('../Images/ceo.jpg')}
              alt="CEO"
              style={{
                width: isMobile ? '100%' : '220px',
                height: '280px',
                borderRadius: '10px',
                objectFit: 'cover',
                objectPosition: 'top',
                flexShrink: 0,
              }}
            />
          </div>
        </div>

        <Footer />

        {/* ── CSS ── */}
        <style>{`
          @media (max-width: 768px) {
            .header-container { height: 40vh !important; }
          }
          .text-item { position: relative; display: inline-block; }
          .text-item::after {
            content: '';
            display: block;
            width: 0%;
            height: 2px;
            background-color: #003366;
            transition: width 0.3s ease;
            position: absolute;
            bottom: -5px;
            left: 0;
          }
          .text-item:hover::after { width: 100%; }
          *::-webkit-scrollbar { display: none; }
          * { scrollbar-width: none; }
          body { overflow-x: hidden; }
        `}</style>
      </div>
    </>
  );
};

export default About;